import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Plus, Pencil, Trash2, X, MapPin, Check } from "lucide-react";

const COUNTRIES = [
  "Indonesia","Malaysia","Singapore","Philippines","Thailand","Vietnam","Cambodia","Myanmar",
  "Brunei","Laos","Australia","Japan","South Korea","China","India","United States","United Kingdom",
  "Germany","France","Saudi Arabia","UAE","Qatar","Kuwait","Bahrain","Oman"
];

const emptyForm = {
  label: "", recipient_name: "", phone: "", address: "",
  district: "", city: "", province: "", postal_code: "", country: "Indonesia"
};

export default function Addresses() {
  const [addresses, setAddresses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/me/addresses");
      setAddresses(r.data);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await api.put(`/me/addresses/${editing}`, form);
        toast.success("Alamat diperbarui!");
      } else {
        await api.post("/me/addresses", form);
        toast.success("Alamat ditambahkan!");
      }
      setForm(emptyForm);
      setEditing(null);
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  const edit = (addr) => {
    setForm({
      label: addr.label, recipient_name: addr.recipient_name, phone: addr.phone,
      address: addr.address, district: addr.district, city: addr.city,
      province: addr.province, postal_code: addr.postal_code, country: addr.country
    });
    setEditing(addr.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus alamat ini?")) return;
    try {
      await api.delete(`/me/addresses/${id}`);
      toast.success("Alamat dihapus");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="label-mono text-zinc-500">// ALAMAT SAYA</div>
          <h1 className="font-display text-3xl font-black uppercase mt-1">Alamat Tersimpan</h1>
        </div>
        <button onClick={() => { setForm(emptyForm); setEditing(null); setShowForm(!showForm); }}
          className="btn-primary inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Tambah Alamat
        </button>
      </div>

      {showForm && (
        <div className="border border-black bg-white p-6 mb-6 shadow-brutal">
          <div className="flex justify-between items-center mb-4">
            <div className="label-mono">{editing ? "// EDIT ALAMAT" : "// TAMBAH ALAMAT BARU"}</div>
            <button onClick={() => { setShowForm(false); setEditing(null); setForm(emptyForm); }}>
              <X className="w-4 h-4" />
            </button>
          </div>
          <form onSubmit={save} className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="label-mono block mb-2">Label Alamat (contoh: Rumah, Kantor)</label>
              <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                className="input-brutal" placeholder="Rumah / Kantor / dll" required />
            </div>
            <div>
              <label className="label-mono block mb-2">Nama Penerima</label>
              <input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                className="input-brutal" placeholder="Nama lengkap penerima" required />
            </div>
            <div>
              <label className="label-mono block mb-2">No. Handphone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="input-brutal" placeholder="08xxxxxxxxxx" required />
            </div>
            <div className="md:col-span-2">
              <label className="label-mono block mb-2">Alamat Lengkap</label>
              <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                rows={3} className="input-brutal" placeholder="Nama jalan, nomor rumah, RT/RW, kelurahan" required />
            </div>
            <div>
              <label className="label-mono block mb-2">Kecamatan</label>
              <input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })}
                className="input-brutal" placeholder="Kecamatan" required />
            </div>
            <div>
              <label className="label-mono block mb-2">Kota / Kabupaten</label>
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="input-brutal" placeholder="Kota atau Kabupaten" required />
            </div>
            <div>
              <label className="label-mono block mb-2">Provinsi</label>
              <input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })}
                className="input-brutal" placeholder="Provinsi" required />
            </div>
            <div>
              <label className="label-mono block mb-2">Kode Pos</label>
              <input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                className="input-brutal" placeholder="12345" required />
            </div>
            <div>
              <label className="label-mono block mb-2">Negara</label>
              <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="input-brutal" required>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="md:col-span-2 flex gap-3">
              <button type="submit" disabled={loading} className="btn-primary flex items-center gap-2">
                <Check className="w-4 h-4" /> {loading ? "// MENYIMPAN..." : editing ? "Update Alamat" : "Simpan Alamat"}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); setForm(emptyForm); }} className="btn-secondary">Batal</button>
            </div>
          </form>
        </div>
      )}

      {addresses.length === 0 ? (
        <div className="border border-black bg-white p-12 text-center">
          <MapPin className="w-8 h-8 mx-auto mb-3 text-zinc-400" />
          <div className="font-mono text-sm text-zinc-500">// Belum ada alamat tersimpan</div>
          <div className="font-mono text-xs text-zinc-400 mt-1">Tambahkan alamat untuk mempercepat checkout</div>
        </div>
      ) : (
        <div className="space-y-4">
          {addresses.map((addr) => (
            <div key={addr.id} className="border border-black bg-white p-5">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-[#FF6A00]" />
                  <span className="font-mono text-xs font-black uppercase bg-[#FFD600] px-2 py-0.5 border border-black">
                    {addr.label}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => edit(addr)} className="p-1.5 border border-black hover:bg-black hover:text-white">
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button onClick={() => remove(addr.id)} className="p-1.5 border border-black hover:bg-[#FF3333] hover:text-white">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <div className="font-mono text-sm font-bold">{addr.recipient_name}</div>
              <div className="font-mono text-xs text-zinc-500 mt-1">{addr.phone}</div>
              <div className="font-mono text-xs text-zinc-600 mt-2">
                {addr.address}, {addr.district}, {addr.city}, {addr.province} {addr.postal_code}, {addr.country}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}